import { busyRetryAfter } from "../../api/busyRetry";
import { STATUS_COPY } from "../../copy/status";

/**
 * What the chat's error line says when a turn fails (#392 phase 2, CAP-O41).
 *
 * Three failures reach the line, and only the words differ; what happens to the
 * question, the transcript and the composer is the page's, and stays as a failed
 * turn has always had it:
 *
 * 1. **The stream ends on an error event.** `{"error": sentence, "done": true}`.
 *    When the request pool was exhausted at the turn's first database steps the
 *    server adds `"busy": true` (the status was already sent, so it cannot be a
 *    503 and says so itself). The line is then `STATUS_COPY.busyWrite`, the
 *    deck's own words, not the server's generic sentence. A reader that has
 *    never heard of `busy` (the bundle on production before this) reads
 *    `error` alone and shows the sentence, exactly as before.
 * 2. **The POST is refused before the stream starts, as busy.** A 503 with a
 *    parseable `Retry-After` of at most `maxRetryAfterS` (the rule in
 *    `busyRetry.ts`: status plus header, never the words). A chat turn is a
 *    write and is never sent again on its own, so the line hands the next move
 *    to the reader: `busyWrite`. Any other 503 (no header, or asking for
 *    minutes) is not "ask again in a moment" and is shown as it always was.
 * 3. **The POST is refused for any other reason.** The server's JSON bodies put
 *    their sentence in `detail` (`{"detail": "Too many chat requests. Please
 *    wait a moment."}`), and the line used to show the whole body, braces and
 *    all. It now shows the sentence. A body of any other shape (a 422's list of
 *    problems, plain text from a proxy, an empty body) is shown exactly as it was.
 *
 * A fourth is not in the server's hands: the connection fails, or the stream's body
 * ends with neither `done` nor `error`. Nothing of such a turn is final, and its line
 * is `CONNECTION_FAILED_LINE`, the one a failed connection always had (#449).
 *
 * What becomes of the question is not decided here, only the words and the
 * `kind` (owner ruling CAP-O42): a `busy` refusal, which never reached the model
 * and stored nothing, gives the question back to the composer
 * (`useCoachStream` calls `onBusyRefused` for it); every other failure, and the
 * stream's own busy marker (the server has stored that question), leaves it where
 * a failed turn leaves it.
 */

/**
 * The sentence a non-2xx body carries: its `detail` when the body is JSON with a
 * non-blank string `detail`, else the body exactly as received.
 */
export function detailSentence(body: string): string {
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
      const detail = (parsed as { detail?: unknown }).detail;
      if (typeof detail === "string" && detail.trim() !== "") return detail;
    }
  } catch {
    // Not JSON: plain text from a proxy, or nothing at all.
  }
  return body;
}

export type ChatRefusal =
  /** A busy 503: never sent again on its own, and the one refusal whose question goes back to the composer (CAP-O42). */
  | { kind: "busy"; line: string }
  | { kind: "failed"; line: string };

/** How a non-2xx answer to the chat POST reads, once the spent-budget refusal has been ruled out. */
export function chatRefusal(
  status: number,
  retryAfterHeader: string | null,
  body: string,
): ChatRefusal {
  if (busyRetryAfter(status, retryAfterHeader) !== null) {
    return { kind: "busy", line: STATUS_COPY.busyWrite };
  }
  return { kind: "failed", line: detailSentence(body) };
}

/**
 * The line for a turn whose connection failed: the fetch threw, or the body ended without the reply's `done` or an `error`
 * (a stream cut in transit, `contracts/coach-stream.json`'s `cut` ending, in which nothing that arrived is final). The line
 * the page has always shown for a failed connection, kept as it was; it is chrome, English by decision D9, and in no deck.
 */
export const CONNECTION_FAILED_LINE = "Connection to the AI Coach failed. Please try again.";

/** The line for a stream's error event: the deck's busy line when it carries the marker, else the server's sentence. */
export function streamErrorLine(event: { error: string; busy?: boolean }): string {
  return event.busy === true ? STATUS_COPY.busyWrite : event.error;
}
